import { useState } from 'react';

import { documentsApi, foldersApi } from '@frontend/lib/api';
import { useQueryClient } from '@tanstack/react-query';

export function useDocumentUpload() {
  const queryClient = useQueryClient();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const uploadDocument = async ({
    file,
    folderId,
    userId,
  }: {
    file: File;
    folderId: string;
    userId?: string;
  }) => {
    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      // Step 1: Get presigned upload URL
      const fileType = file.name.split('.').pop()?.toUpperCase() as
        | 'PDF'
        | 'DOCX'
        | 'PPTX'
        | 'TXT';

      const presignedData = await documentsApi.getPresignedUploadUrl({
        filename: file.name,
        fileType,
        fileSize: file.size,
        userId,
      });

      setUploadProgress(25);

      // Step 2: Upload file to S3 using presigned POST
      const formData = new FormData();
      Object.entries(presignedData.uploadFields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', file);

      const uploadResponse = await fetch(presignedData.uploadUrl, {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to S3');
      }

      setUploadProgress(50);

      // Step 3: Create document record in DynamoDB under the folder
      await foldersApi.createDocument(folderId, {
        documentId: presignedData.documentId,
        s3Key: presignedData.s3Key,
        filename: file.name,
        fileType,
        fileSize: file.size,
      });

      setUploadProgress(75);

      // Step 4: Invalidate folder documents query
      queryClient.invalidateQueries({
        queryKey: ['folderDocuments', folderId],
      });

      setUploadProgress(100);
      setIsUploading(false);

      return {
        documentId: presignedData.documentId,
        s3Key: presignedData.s3Key,
        filename: file.name,
      };
    } catch (err) {
      setIsUploading(false);
      setUploadProgress(0);
      setError(err instanceof Error ? err : new Error('Upload failed'));
      throw err;
    }
  };

  return {
    uploadDocument,
    isUploading,
    uploadProgress,
    error,
  };
}
